/**
 * `AgencyFeatureCard` — ADR-0237 §5 ops toggles (agency, white_label).
 *
 * Two toggles behind one admin-role gate:
 *   - Agency: lets the account's users switch into linked client
 *     accounts and see the Clients page. Turning it OFF immediately
 *     suspends every acting session for the agency (next-request
 *     revocation re-check at the server).
 *   - White label: an upsell; only toggleable when Agency is ON.
 *
 * Follows the SupportLoginCard shape:
 *   - fetches the current `{ agency, white_label }` on mount from
 *     `GET /internal/admin/accounts/:id/features`.
 *   - renders a short card with two labelled toggles.
 *   - a confirm dialog gates the "Turn Agency OFF" write because the
 *     downstream effect on acting sessions is immediate.
 *   - 'support' role sees the card in read-only mode; the server
 *     also 403s so this is UX-only.
 */

import { useEffect, useState } from 'react';

import type { StaffRole } from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { hasRole } from '../../lib/roles.js';
import { useC } from '../../lib/theme.js';
import type { AdminAccountFeatures } from '../../api/admin-client.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface AgencyFeatureCardProps {
  accountId: string;
  staffRole: StaffRole;
}

export function AgencyFeatureCard({
  accountId,
  staffRole,
}: AgencyFeatureCardProps): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();
  // Server role-gates writes + reads to 'admin'
  // (STAFF_ROUTE_MIN_ROLE in services/api/src/http/routes/internal/
  // admin/account-features.ts). The SPA mirrors: 'support' sees
  // nothing (writes would 403; reads would 403 too so hiding is
  // honest).
  const canWrite = hasRole(staffRole, 'admin');

  const [features, setFeatures] = useState<AdminAccountFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<null | 'agency' | 'white_label'>(null);
  const [confirmTurnOff, setConfirmTurnOff] = useState(false);

  useEffect(() => {
    // Server also 403s a non-admin read; skip the fetch so the
    // console doesn't log a 403 for 'support' who sees nothing.
    if (!canWrite) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getAccountFeatures(accountId);
        if (!cancelled) setFeatures(res);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === 'unauthorized') return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setError(`Could not load features${reason}.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, accountId, canWrite]);

  // Hide the whole card for 'support' — they can neither read nor write.
  if (!canWrite) return null;

  const setAgency = async (next: boolean): Promise<void> => {
    if (pending !== null) return;
    setPending('agency');
    setError(null);
    try {
      const after = await api.setAccountAgencyFeature(accountId, next);
      setFeatures(after);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not update Agency${reason}.`);
    } finally {
      setPending(null);
    }
  };

  const setWhiteLabel = async (next: boolean): Promise<void> => {
    if (pending !== null) return;
    setPending('white_label');
    setError(null);
    try {
      const after = await api.setAccountWhiteLabelFeature(accountId, next);
      setFeatures(after);
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not update White label${reason}.`);
    } finally {
      setPending(null);
    }
  };

  const agency_on = features?.agency ?? false;
  const white_label_on = features?.white_label ?? false;
  // White label disabled unless Agency is on.
  const white_label_toggleable = agency_on;

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Agency features
        </div>
        {loading ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 1,
            }}
          >
            Loading…
          </span>
        ) : null}
      </div>

      <ToggleRow
        label="Agency"
        description="Lets this account act inside linked client accounts and see the Clients page. Turning OFF suspends every acting session on the next request."
        value={agency_on}
        pending={pending === 'agency'}
        disabled={loading}
        onChange={(next) => {
          if (!next && agency_on) {
            setConfirmTurnOff(true);
            return;
          }
          void setAgency(next);
        }}
      />

      <div style={{ height: 1, background: C.borderSoft, margin: '14px 0' }} />

      <ToggleRow
        label="White label"
        description={
          white_label_toggleable
            ? 'Rebrands non-signed surfaces (reports, report emails, dashboard chrome). Signed artifacts always identify Sill as signer.'
            : 'Enable Agency first.'
        }
        value={white_label_on}
        pending={pending === 'white_label'}
        disabled={loading || !white_label_toggleable}
        onChange={(next) => void setWhiteLabel(next)}
      />

      {error !== null ? (
        <div
          className="mt-3"
          style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}
        >
          {error}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmTurnOff}
        title="Turn Agency off?"
        body={
          <>
            Agency access to all linked client accounts stops immediately on the next request. Links are kept — turning Agency back on restores them without data loss.
          </>
        }
        tone="coral"
        confirmLabel="Turn off"
        pending={pending === 'agency'}
        onConfirm={() => {
          setConfirmTurnOff(false);
          void setAgency(false);
        }}
        onCancel={() => {
          if (pending === 'agency') return;
          setConfirmTurnOff(false);
        }}
      />
    </Card>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  value: boolean;
  pending: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}

function ToggleRow({
  label,
  description,
  value,
  pending,
  disabled,
  onChange,
}: ToggleRowProps): JSX.Element {
  const C = useC();
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          {label}
          <Pill tone={value ? 'lime' : 'coral'}>{value ? 'ON' : 'OFF'}</Pill>
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {description}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={`Toggle ${label}`}
        disabled={disabled || pending}
        onClick={() => onChange(!value)}
        style={{
          flexShrink: 0,
          width: 42,
          height: 24,
          borderRadius: 12,
          background: value ? C.lime : C.surface3,
          border: `1px solid ${C.border}`,
          cursor: disabled || pending ? 'not-allowed' : 'pointer',
          position: 'relative',
          opacity: disabled || pending ? 0.6 : 1,
          padding: 0,
          minHeight: 24,
        }}
      >
        <span
          aria-hidden
          style={{
            position: 'absolute',
            top: 1,
            left: value ? 19 : 1,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: value ? C.ink : C.body,
            transition: 'left 150ms ease',
          }}
        />
      </button>
    </div>
  );
}
